import {createContext, useContext, useEffect, useState, ReactNode} from 'react';

import {fetchAndActivate, getAll, RemoteConfig} from 'firebase/remote-config';

import {mobileFirebaseApp, registerRemoteConfig, webFirebaseApp} from 'config/firebase';
import {RemoteConfigDefaults} from 'config/remoteConfigDefaults';
import {RemoteConfigNames} from 'config/remoteConfigNames';
import {isDevelopmentMode} from 'config/staticConfig';
import {ensighten} from 'shared/Ensighten/components';
import {getValuesByKey} from 'utils/types';

const webRemoteConfig = registerRemoteConfig(webFirebaseApp);
const mobileRemoteConfig = registerRemoteConfig(mobileFirebaseApp);

interface RemoteConfigMetaData {
  IsLoading?: boolean;
  HasError?: any;
}
export type RemoteConfigValues = RemoteConfigMetaData & typeof RemoteConfigDefaults;
type IncompleteRemoteConfigValues = Partial<RemoteConfigValues>;

const fetchDotEnvFeatureFlagOverrides = (include: boolean): Promise<IncompleteRemoteConfigValues> =>
  new Promise<IncompleteRemoteConfigValues>((resolve) => {
    if (!include) {
      resolve({});
      return;
    }

    resolve(
      Object.keys(RemoteConfigNames).reduce<IncompleteRemoteConfigValues>((overrides, key) => {
        const remoteConfigName = getValuesByKey(RemoteConfigNames, key);

        if (remoteConfigName && process.env[remoteConfigName]) {
          return {
            ...overrides,
            [key]: process.env[remoteConfigName]
          };
        }
        return overrides;
      }, {})
    );
  });

const handleRemoteConfigError = (error: Error, resolveCallback: any) => {
  ensighten.ensightenLogger({logInfo: `name=${error.name} error=${error.message}`});
  resolveCallback({HasError: true});
};

const fetchRemoteConfig = (appConfig: Promise<RemoteConfig | undefined>): Promise<IncompleteRemoteConfigValues> =>
  new Promise<IncompleteRemoteConfigValues>((resolve) => {
    appConfig
      .then((remoteConfig) => {
        if (remoteConfig) {
          fetchAndActivate(remoteConfig)
            .then(() => getAll(remoteConfig))
            .then((config) => {
              resolve(
                Object.keys(config).reduce<IncompleteRemoteConfigValues>(
                  (values, key) => ({
                    ...values,
                    [key]: config[key].asString()
                  }),
                  {}
                )
              );
            })
            .catch((error) => {
              handleRemoteConfigError(error, resolve);
            });
        } else {
          resolve({});
        }
      })
      .catch((error) => {
        handleRemoteConfigError(error, resolve);
      });
  });

const RemoteConfigContext = createContext<RemoteConfigValues>(RemoteConfigDefaults);

interface Props {
  children: ReactNode;
}

export const useRemoteConfig = () => useContext(RemoteConfigContext);

const RemoteConfigProvider = ({children}: Props) => {
  const [remoteConfigValues, setRemoteConfigValues] = useState<RemoteConfigValues>(RemoteConfigDefaults);

  useEffect(() => {
    setRemoteConfigValues((currentValues) => ({...currentValues, IsLoading: true, HasError: undefined}));
    Promise.all<IncompleteRemoteConfigValues>([
      fetchRemoteConfig(webRemoteConfig),
      fetchDotEnvFeatureFlagOverrides(!isDevelopmentMode),
      fetchRemoteConfig(mobileRemoteConfig),
      fetchDotEnvFeatureFlagOverrides(isDevelopmentMode)
    ]).then((values) => {
      setRemoteConfigValues({
        ...RemoteConfigDefaults,
        ...values.reduce<IncompleteRemoteConfigValues>(
          (mergedRemoteValues, remoteValues) => ({...mergedRemoteValues, ...remoteValues}),
          {}
        ),
        IsLoading: false
      });
    });
  }, []);

  return <RemoteConfigContext.Provider value={remoteConfigValues}>{children}</RemoteConfigContext.Provider>;
};

export default RemoteConfigProvider;
